import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';

import { getCurrentPosition } from '@/components/shared/utility/location';
import { normalisePathname } from '@/components/shared/utility/router';
import logger from '@/lib/logger';
import clientAxios from '@/services/contentstack/client';
import { trackDealerSearch, trackFBDealerSearch } from '@/tag-manager';
import { IDealer } from '@/types/dealer/dealer';
import { IDistrict } from '@/types/dealer/district';
import { ILocation } from '@/types/dealer/location';
import { IBody, IVehicleType } from '@/types/services/contentstack';

export interface ISubmitSearchPayload {
  provinceID?: string;
  districtIDs?: string;
  searchText?: string;
  vehicleType?: IVehicleType;
  showroom?: boolean;
  service?: boolean;
  bodyPaint?: boolean;
  changeTire?: boolean;
  overhaul?: boolean;
}

interface UseDealerSearchProps {
  provinceOptions: any[];
  vehicleType: IVehicleType;
  isSetPath?: boolean;
}

export const useDealerSearch = ({
  provinceOptions,
  isSetPath = true,
}: UseDealerSearchProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [currentPosition, setCurrentPosition] = useState<ILocation | null>(
    null
  );
  const [selectedProvinceID, setSelectedProvinceID] = useState<string>('');
  const [selectedDistrictID, setSelectedDistrictID] = useState<string>('');
  const [isLoadingDealer, setIsLoadingDealer] = useState<boolean>(false);
  const [districts, setDistricts] = useState<IDistrict[]>([]);
  const [dealers, setDealers] = useState<IDealer[]>([]);
  const [isSearched, setIsSearched] = useState<boolean>(false);

  const getResettedSearchFilterRouteQuery = (vehicleType: IVehicleType) => {
    const queryParams = new URLSearchParams(searchParams?.toString());
    queryParams.set('vehicle', encodeURIComponent(vehicleType));
    return queryParams;
  };

  const getSearchFilterRouteQuery = (searchFilter: ISubmitSearchPayload) => {
    const queryParams = new URLSearchParams(searchParams?.toString());
    queryParams.delete('path');
    queryParams.set(
      'vehicle',
      encodeURIComponent(searchFilter.vehicleType as string)
    );
    const provinceId = selectedProvinceID || searchFilter.provinceID;
    if (provinceId) {
      queryParams.set('province', encodeURIComponent(provinceId));
    }
    const districtId = selectedDistrictID || searchFilter.districtIDs;
    if (districtId) {
      queryParams.set('district', encodeURIComponent(districtId));
    }
    if (searchFilter.showroom) {
      queryParams.set('showroom', '1');
    } else {
      queryParams.delete('showroom');
    }
    if (searchFilter.service) {
      queryParams.set('service', '1');
    } else {
      queryParams.delete('service');
    }
    return queryParams;
  };

  const resetSearchFilterData = () => {
    setSelectedProvinceID('');
    setSelectedDistrictID('');
  };

  const onProvinceChange = (value: string) => {
    setSelectedDistrictID('');
    setSelectedProvinceID(value);
  };

  const onDistrictChange = (value: string) => {
    setSelectedDistrictID(value);
  };

  const getProvinceName = (id: string) => {
    const foundedProvince = provinceOptions.find((o) => o.uid == id);
    return foundedProvince ? foundedProvince.name_th : '';
  };

  const getDistrictName = (id: string) => {
    const foundedDistrict = districts.find((o) => o.uid == id);
    return foundedDistrict ? foundedDistrict.name_th : '';
  };

  const loadDistrictsData = async () => {
    try {
      const body: IBody = {
        route: 'getDistrictByProvinceID',
        id: selectedProvinceID,
      };
      const res = await clientAxios({
        data: body,
      });
      setDistricts(res.data.entries);
    } catch (e) {
      logger.error(e);
      setDistricts([]);
    }
  };

  const submitSearch = ({
    searchText = '',
    vehicleType = 'lcv',
    showroom = false,
    service = false,
    bodyPaint = false,
    changeTire = false,
    overhaul = false,
    provinceID,
    districtIDs,
  }: ISubmitSearchPayload) => {
    const payload: ISubmitSearchPayload = {
      searchText,
      vehicleType,
      showroom,
      service,
      bodyPaint,
      changeTire,
      overhaul,
      provinceID,
      districtIDs,
    };
    searchDealers(payload);
  };

  const searchCurrentLocation = (vehicleType: IVehicleType) => {
    resetSearchFilterData();
    const query = getResettedSearchFilterRouteQuery(vehicleType);
    isSetPath &&
      router.push(`${normalisePathname(pathname)}?${query.toString()}`, {
        scroll: false,
      });
    searchCurrentPosition(vehicleType);
  };

  const setTrackingData = (searchFilter: ISubmitSearchPayload) => {
    trackDealerSearch({
      province: getProvinceName(searchFilter.provinceID ?? selectedProvinceID),
      district: getDistrictName(searchFilter.districtIDs ?? selectedDistrictID),
      search: searchFilter.searchText ?? '',
      vehicleType: searchFilter.vehicleType ?? '',
      showroom: searchFilter.showroom ?? false,
      service: searchFilter.service ?? false,
      bodyPaint: searchFilter.bodyPaint ?? false,
      changeTire: searchFilter.changeTire ?? false,
      overhaul: searchFilter.overhaul ?? false,
    });
    trackFBDealerSearch({
      city: getProvinceName(selectedProvinceID),
      district: getDistrictName(selectedDistrictID),
    });
  };

  const getDealers = async (searchFilter: ISubmitSearchPayload) => {
    setTrackingData(searchFilter);
    try {
      const body: IBody = {
        route: 'getDealersWithQuery',
        provinceID: searchFilter.provinceID ?? selectedProvinceID,
        districtIDs: searchFilter.districtIDs ?? selectedDistrictID,
        searchText: searchFilter.searchText,
        vehicleType: searchFilter.vehicleType,
        showroom: searchFilter.showroom,
        service: searchFilter.service,
        bodyPaint: searchFilter.bodyPaint,
        changeTire: searchFilter.changeTire,
        overhaul: searchFilter.overhaul,
      };
      const res = await clientAxios({
        data: body,
      });

      if (res.status === 204) {
        alert('ขออภัยระบบขัดข้อง กรุณาค้นหาใหม่อีกครั้ง');
      }

      setDealers(res.data?.entries ?? []);
    } catch {
      setDealers([]);
    }
  };

  const searchCurrentPosition = (vehicleType: IVehicleType) => {
    if (isLoadingDealer) return;

    setIsSearched(true);
    setIsLoadingDealer(true);
    const successCallback = async (position: ILocation) => {
      setCurrentPosition(position);
      try {
        const body: IBody = {
          route: 'getDealerWithLatLng',
          lat: position.lat,
          lng: position.lng,
          vehicleType,
        };
        const res = await clientAxios({
          data: body,
        });
        setDealers(res.data?.entries ?? []);
        setIsLoadingDealer(false);
      } catch (e) {
        logger.error(e);
        setDealers([]);
        setIsLoadingDealer(false);
      }
    };
    const errorCallback = () => {
      setIsLoadingDealer(false);
      setIsSearched(true);
    };

    getCurrentPosition(successCallback, errorCallback);
  };

  const searchDealers = (searchFilter: ISubmitSearchPayload) => {
    if (isLoadingDealer) return;

    setIsSearched(true);
    setIsLoadingDealer(true);
    getDealers(searchFilter)
      .then(() => {
        const query = getSearchFilterRouteQuery(searchFilter);
        isSetPath &&
          router.push(`${normalisePathname(pathname)}?${query.toString()}`, {
            scroll: false,
          });
      })
      .finally(() => {
        setIsLoadingDealer(false);
      });
  };

  useEffect(() => {
    if (!selectedProvinceID) {
      setDistricts([]);
      return;
    }

    loadDistrictsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvinceID]);

  return {
    isSearched,
    isLoadingDealer,
    currentPosition,
    dealers,
    districts,
    selectedProvinceID,
    selectedDistrictID,
    submitSearch,
    searchCurrentLocation,
    onProvinceChange,
    onDistrictChange,
  };
};
